import { MoreVerticalIcon } from '@blocksuite/icons/rc';
import { notify } from '@pique/component';
import type {
  InviteModalProps,
  PaginationProps,
} from '@pique/component/member-components';
import { InviteModal, Pagination } from '@pique/component/member-components';
import { SettingRow } from '@pique/component/setting-components';
import { Avatar } from '@pique/component/ui/avatar';
import { Button, IconButton } from '@pique/component/ui/button';
import { Loading } from '@pique/component/ui/loading';
import { Menu, MenuItem } from '@pique/component/ui/menu';
import { PiqueErrorBoundary } from '@pique/core/components/pique/pique-error-boundary';
import { useInviteMember } from '@pique/core/hooks/pique/use-invite-member';
import { useMemberCount } from '@pique/core/hooks/pique/use-member-count';
import type { Member } from '@pique/core/hooks/pique/use-members';
import { useMembers } from '@pique/core/hooks/pique/use-members';
import { useRevokeMemberPermission } from '@pique/core/hooks/pique/use-revoke-member-permission';
import { WorkspacePermissionService } from '@pique/core/modules/permissions';
import { WorkspaceFlavour } from '@pique/env/workspace';
import { Permission } from '@pique/graphql';
import { useI18n } from '@pique/i18n';
import {
  useEnsureLiveData,
  useLiveData,
  useService,
  WorkspaceService,
} from '@toeverything/infra';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type AuthAccountInfo,
  AuthService,
} from '../../../../../modules/cloud';
import * as style from './style.css';

const COUNT_PER_PAGE = 8;
type OnRevoke = (memberId: string) => void;
const MembersPanelLocal = () => {
  const t = useI18n();
  return (
    <SettingRow name={`${t['Members']()} (0)`} desc={t['Members hint']()}>
      <Button>{t['Invite Members']()}</Button>
    </SettingRow>
  );
};

export const CloudWorkspaceMembersPanel = () => {
  const workspace = useService(WorkspaceService).workspace;
  const memberCount = useMemberCount(workspace.id);

  const permissionService = useService(WorkspacePermissionService);
  const isOwner = useLiveData(permissionService.permission.isOwner$);
  useEffect(() => {
    permissionService.permission.revalidate();
  }, [permissionService]);

  const t = useI18n();
  const { invite, isMutating } = useInviteMember(workspace.id);
  const revokeMemberPermission = useRevokeMemberPermission(workspace.id);

  const [open, setOpen] = useState(false);
  const [memberSkip, setMemberSkip] = useState(0);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);

  const onPageChange = useCallback<PaginationProps['onPageChange']>(
    (offset: number) => {
      setMemberSkip(offset);
    },
    []
  );

  const onInviteConfirm = useCallback<InviteModalProps['onConfirm']>(
    async ({
      email,
      permission,
    }: {
      email: string;
      permission: Permission;
    }) => {
      const success = await invite(
        email,
        permission,
        // send invite email
        true
      );
      if (success) {
        notify.success({
          title: t['Invitation sent'](),
          message: t['Invitation sent hint'](),
        });
        setOpen(false);
      }
    },
    [invite, t]
  );

  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const [memberListHeight, setMemberListHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (
      memberCount > COUNT_PER_PAGE &&
      listContainerRef.current &&
      memberListHeight === null
    ) {
      const rect = listContainerRef.current.getBoundingClientRect();
      setMemberListHeight(rect.height);
    }
  }, [listContainerRef, memberCount, memberListHeight]);

  const onRevoke = useCallback<OnRevoke>(
    async memberId => {
      const res = await revokeMemberPermission(memberId);
      if (res?.revoke) {
        notify.success({ title: t['Removed successfully']() });
      }
    },
    [revokeMemberPermission, t]
  );

  return (
    <>
      <SettingRow
        name={`${t['Members']()} (${memberCount})`}
        desc={t['Members hint']()}
        spreadCol={!!isOwner}
      >
        {isOwner ? (
          <>
            <Button onClick={openModal}>{t['Invite Members']()}</Button>
            <InviteModal
              open={open}
              setOpen={setOpen}
              onConfirm={onInviteConfirm}
              isMutating={isMutating}
            />
          </>
        ) : null}
      </SettingRow>

      <div
        className={style.membersPanel}
        ref={listContainerRef}
        style={memberListHeight ? { height: memberListHeight } : {}}
      >
        <Suspense fallback={<MemberListFallback memberCount={memberCount} />}>
          <MemberList
            workspaceId={workspace.id}
            isOwner={!!isOwner}
            skip={memberSkip}
            onRevoke={onRevoke}
          />
        </Suspense>

        {memberCount > COUNT_PER_PAGE && (
          <Pagination
            totalCount={memberCount}
            countPerPage={COUNT_PER_PAGE}
            onPageChange={onPageChange}
          />
        )}
      </div>
    </>
  );
};
export const MembersPanelFallback = () => {
  const t = useI18n();

  return (
    <>
      <SettingRow
        name={t['Members']()}
        desc={t['com.pique.limits.members.prompt']()}
      />
      <div className={style.membersPanel}>
        <MemberListFallback memberCount={1} />
      </div>
    </>
  );
};

const MemberListFallback = ({ memberCount }: { memberCount: number }) => {
  // prevent page jitter
  const height = useMemo(() => {
    if (memberCount > COUNT_PER_PAGE) {
      // height and margin-bottom
      return COUNT_PER_PAGE * 58 + (COUNT_PER_PAGE - 1) * 6;
    }
    return 'auto';
  }, [memberCount]);
  const t = useI18n();

  return (
    <div
      style={{
        height,
      }}
      className={style.membersFallback}
    >
      <Loading size={20} />
      <span>{t['com.pique.settings.member.loading']()}</span>
    </div>
  );
};

const MemberList = ({
  workspaceId,
  isOwner,
  skip,
  onRevoke,
}: {
  workspaceId: string;
  isOwner: boolean;
  skip: number;
  onRevoke: OnRevoke;
}) => {
  const members = useMembers(workspaceId, skip, COUNT_PER_PAGE);
  const session = useService(AuthService).session;
  const account = useEnsureLiveData(session.account$);

  return (
    <div className={style.memberList}>
      {members.map((member: Member) => (
        <MemberItem
          currentAccount={account}
          key={member['id']}
          member={member}
          isOwner={isOwner}
          onRevoke={onRevoke}
        />
      ))}
    </div>
  );
};

const MemberItem = ({
  member,
  isOwner,
  currentAccount,
  onRevoke,
}: {
  member: Member;
  isOwner: boolean;
  currentAccount: AuthAccountInfo;
  onRevoke: OnRevoke;
}) => {
  const t = useI18n();

  const handleRevoke = useCallback(() => {
    onRevoke(member['id']);
  }, [onRevoke, member['id']]);

  const operationButtonInfo = useMemo(() => {
    return {
      show: isOwner && currentAccount.id !== member['id'],
      leaveOrRevokeText: t['Remove from workspace'](),
    };
  }, [currentAccount.id, isOwner, member['id'], t]);

  return (
    <div
      key={member['id']}
      className={style.memberListItem}
      data-testid="member-item"
    >
      <Avatar
        size={36}
        url={member['avatarUrl']}
        name={(member['name'] ? member['name'] : member['email']) as string}
      />
      <div className={style.memberContainer}>
        {member['name'] ? (
          <>
            <div className={style.memberName}>{member['name']}</div>
            <div className={style.memberEmail}>{member['email']}</div>
          </>
        ) : (
          <div className={style.memberName}>{member['email']}</div>
        )}
      </div>
      <div
        className={clsx(style.roleOrStatus, {
          pending: !member['accepted'],
        })}
      >
        {member['accepted']
          ? member['permission'] === Permission.Owner
            ? 'Workspace Owner'
            : 'Member'
          : 'Pending'}
      </div>
      <Menu
        items={
          <MenuItem data-member-id={member['id']} onClick={handleRevoke}>
            {operationButtonInfo.leaveOrRevokeText}
          </MenuItem>
        }
      >
        <IconButton
          disabled={!operationButtonInfo.show}
          style={{
            visibility: operationButtonInfo.show ? 'visible' : 'hidden',
            flexShrink: 0,
          }}
        >
          <MoreVerticalIcon />
        </IconButton>
      </Menu>
    </div>
  );
};

export const MembersPanel = (): ReactElement | null => {
  const workspace = useService(WorkspaceService).workspace;
  if (workspace.flavour === WorkspaceFlavour.LOCAL) {
    return <MembersPanelLocal />;
  }
  return (
    <PiqueErrorBoundary>
      <Suspense fallback={<MembersPanelFallback />}>
        <CloudWorkspaceMembersPanel />
      </Suspense>
    </PiqueErrorBoundary>
  );
};
